import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, RefreshCw, Save, Settings, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  getResumoComissaoCarregadores,
  listarParceirosComComissaoPorModelo,
  listarComissaoGlobais,
  listarComissoesParceiroPai,
  obterMinhaComissaoPorModelo,
  salvarComissaoParceiroModelo,
  atualizarComissaoGlobal,
  salvarComissaoParceiroPai,
  listarParceirosRedeComComissaoPorModelo,
  listarMinhasComissoesParceiroPai,
  getResumoComissaoCarregadoresRede,
  type ResumoComissaoCarregador,
  type ParceiroComissaoPorModelo,
  type ParceiroPaiComissao,
  type ParceiroFilhoComissaoPai,
  type ComissaoGlobal,
  type ModeloCarregador,
} from "@/services/carregadoresComissaoService";
import {
  listarValoresPropostasGlobais,
  atualizarValoresPropostasGlobais,
  criarNovoModeloValoresPropostasGlobais,
  removerModeloValoresPropostasGlobais,
  listarValoresPropostasPorParceiro,
  atualizarValoresPropostasParceiro,
  type ValoresPropostaGlobal,
  type ValoresPropostaParceiro,
} from "@/services/carregadoresValoresPropostasService";
import { formatBrazilianCurrency, formatBrazilianNumber } from "@/utils/formatters";
import { useIsMobile } from "@/hooks/use-mobile";

type EditValores = Record<string, { "7": string; "11": string; "22": string }>;
type EditValoresPropostas = Record<string, { equipamento: string; instalacao: string; materiais: string }>;
type EditValoresPropostasParceiro = Record<string, Record<string, { equipamento: string; instalacao: string; materiais: string }>>;
/** parceiro_pai_id -> parceiro_filho_id -> { "7"|"11"|"22" -> string } */
type EditComissoesParceiroPai = Record<string, Record<string, { "7": string; "11": string; "22": string }>>;

interface ComissoesCarregadoresTabProps {
  isAdmin: boolean;
  parceiroId: string | null;
  canManage: boolean;
  podeCriarRedeParceiros?: boolean;
  onHasUnsavedChanges?: (has: boolean) => void;
}

export function ComissoesCarregadoresTab({ isAdmin, parceiroId, canManage, podeCriarRedeParceiros = false, onHasUnsavedChanges }: ComissoesCarregadoresTabProps) {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(true);
  const [resumos, setResumos] = useState<ResumoComissaoCarregador[]>([]);
  const [configPorModelo, setConfigPorModelo] = useState<ParceiroComissaoPorModelo[]>([]);
  const [minhaConfig, setMinhaConfig] = useState<ParceiroComissaoPorModelo | null>(null);
  const [editandoValores, setEditandoValores] = useState<EditValores>({});
  const [savingSection, setSavingSection] = useState<"propostasParceiro" | "propostasGlobais" | "comissaoGlobais" | "comissoes" | "comissaoPai" | null>(null);
  
  // Estados para valores de propostas globais
  const [valoresPropostas, setValoresPropostas] = useState<ValoresPropostaGlobal[]>([]);
  const [editandoValoresPropostas, setEditandoValoresPropostas] = useState<EditValoresPropostas>({});
  
  // Estados para valores de propostas por parceiro
  const [valoresPropostasParceiro, setValoresPropostasParceiro] = useState<ValoresPropostaParceiro[]>([]);
  const [editandoValoresPropostasParceiro, setEditandoValoresPropostasParceiro] = useState<EditValoresPropostasParceiro>({});
  
  // Estados dirty por seção (alterações não salvas)
  const [dirtyPropostasParceiro, setDirtyPropostasParceiro] = useState(false);
  const [dirtyPropostasGlobais, setDirtyPropostasGlobais] = useState(false);
  const [dirtyComissoes, setDirtyComissoes] = useState(false);
  
  // Parceiros expandidos na seção Valores por Parceiro
  const [expandedParceirosPropostas, setExpandedParceirosPropostas] = useState<Record<string, boolean>>({});

  // Parceiros expandidos na seção Resumo de Comissões por Parceiro (linha "Comissões como parceiro pai")
  const [expandedResumoComissao, setExpandedResumoComissao] = useState<Record<string, boolean>>({});

  // Comissões por Parceiro Pai (apenas admin)
  const [comissoesParceiroPai, setComissoesParceiroPai] = useState<ParceiroPaiComissao[]>([]);
  const [editandoComissoesParceiroPai, setEditandoComissoesParceiroPai] = useState<EditComissoesParceiroPai>({});
  const [expandedParceirosPaiComissao, setExpandedParceirosPaiComissao] = useState<Record<string, boolean>>({});
  const [dirtyComissaoPai, setDirtyComissaoPai] = useState(false);

  // Valores padrão globais de comissões (parceiros e parceiros pai)
  const [comissaoGlobais, setComissaoGlobais] = useState<ComissaoGlobal[]>([]);
  const [editandoComissaoGlobais, setEditandoComissaoGlobais] = useState<Record<string, { parceiro: string; parceiroPai: string }>>({});
  const [dirtyComissaoGlobais, setDirtyComissaoGlobais] = useState(false);
  
  // Estados para diálogo de novo modelo
  const [isNovoModeloOpen, setIsNovoModeloOpen] = useState(false);
  const [novoModelo, setNovoModelo] = useState({ nome: "", equipamento: "", instalacao: "", materiais: "" });
  const [criandoModelo, setCriandoModelo] = useState(false);
  const [removendoModelo, setRemovendoModelo] = useState<string | null>(null);

  const parseValor = (raw: string) => {
    const r = (raw ?? "0").replace(/\./g, "").replace(",", ".");
    return Math.round(parseFloat(r || "0") * 100) / 100;
  };

  // Mapas auxiliares para cálculo de mínimos globais por modelo na seção
  // "Valores de Propostas por Parceiro"
  const mapaValoresGlobais = useMemo(() => {
    const map: Record<string, ValoresPropostaGlobal> = {};
    valoresPropostas.forEach((v) => {
      map[v.modelo_carregador] = v;
    });
    return map;
  }, [valoresPropostas]);

  const mapaComissaoGlobais = useMemo(() => {
    const map: Record<string, ComissaoGlobal> = {};
    comissaoGlobais.forEach((c) => {
      map[c.modelo_carregador] = c;
    });
    return map;
  }, [comissaoGlobais]);

  const getMinimosPropostaModelo = (modelo: string) => {
    const g = mapaValoresGlobais[modelo];
    const cg = mapaComissaoGlobais[modelo];
    const equipamentoMin = g?.preco_equipamento ?? 0;
    const instalacaoMin =
      (g?.preco_instalacao ?? 0) +
      (cg?.valor_comissao_parceiro ?? 0) +
      (cg?.valor_comissao_parceiro_pai ?? 0);
    const materiaisMin = g?.preco_materiais ?? 0;
    return { equipamentoMin, instalacaoMin, materiaisMin };
  };

  const carregar = useCallback(async () => {
    if (!parceiroId && !isAdmin) return;
    setLoading(true);
    try {
      const isParceiroPaiComRede = !isAdmin && parceiroId && podeCriarRedeParceiros;
      
      const promises: Promise<any>[] = [
        isParceiroPaiComRede 
          ? Promise.all([
              getResumoComissaoCarregadores(parceiroId),
              getResumoComissaoCarregadoresRede(parceiroId),
            ]).then(([parentResumos, filhosResumos]) => {
              const parent = Array.isArray(parentResumos) && parentResumos.length > 0 ? parentResumos[0] : null;
              const filhos = Array.isArray(filhosResumos) ? filhosResumos : [];
              return parent ? [parent, ...filhos] : filhos;
            })
          : getResumoComissaoCarregadores(isAdmin ? null : parceiroId),
        isAdmin 
          ? listarParceirosComComissaoPorModelo() 
          : isParceiroPaiComRede
          ? Promise.all([
              obterMinhaComissaoPorModelo(parceiroId),
              listarParceirosRedeComComissaoPorModelo(parceiroId),
            ]).then(([parentConfig, filhosConfig]) => {
              const filhos = Array.isArray(filhosConfig) ? filhosConfig : [];
              return parentConfig ? [parentConfig, ...filhos] : filhos;
            })
          : (parceiroId ? obterMinhaComissaoPorModelo(parceiroId) : Promise.resolve(null)),
      ];
      
      // Carregar valores de propostas globais, por parceiro e comissões parceiro pai apenas se for admin
      if (isAdmin) {
        promises.push(listarValoresPropostasGlobais());
        promises.push(listarValoresPropostasPorParceiro());
        promises.push(listarComissaoGlobais());
        promises.push(listarComissoesParceiroPai());
      } else if (isParceiroPaiComRede) {
        // Parceiro pai com rede: carregar suas comissões de parceiro pai
        promises.push(Promise.resolve(null)); // valoresPropostasGlobais
        promises.push(Promise.resolve(null)); // valoresPropostasParceiro
        promises.push(Promise.resolve(null)); // comissaoGlobais
        promises.push(listarMinhasComissoesParceiroPai(parceiroId));
      }
      
      const results = await Promise.all(promises);
      const resumoData = results[0];
      const configData = results[1];
      const valoresPropostasData = isAdmin ? results[2] : null;
      const valoresPropostasParceiroData = isAdmin ? results[3] : null;
      const comissaoGlobaisData = isAdmin ? results[4] : null;
      const comissoesParceiroPaiData = isAdmin ? results[5] : (isParceiroPaiComRede ? results[5] : null);
      
      setResumos(resumoData);
      if ((isAdmin || isParceiroPaiComRede) && Array.isArray(configData)) {
        setConfigPorModelo(configData);
        const edit: EditValores = {};
        configData.forEach((c) => {
          edit[c.id] = {
            "7": formatBrazilianNumber(c.valor_7kw, 2),
            "11": formatBrazilianNumber(c.valor_11kw, 2),
            "22": formatBrazilianNumber(c.valor_22kw, 2),
          };
        });
        setEditandoValores(edit);
      } else if (parceiroId && configData && !Array.isArray(configData)) {
        setMinhaConfig(configData);
      }
      
      if (isAdmin && valoresPropostasData) {
        setValoresPropostas(valoresPropostasData);
        const editPropostas: EditValoresPropostas = {};
        valoresPropostasData.forEach((v: ValoresPropostaGlobal) => {
          editPropostas[v.modelo_carregador] = {
            equipamento: formatBrazilianNumber(v.preco_equipamento, 2),
            instalacao: formatBrazilianNumber(v.preco_instalacao, 2),
            materiais: formatBrazilianNumber(v.preco_materiais, 2),
          };
        });
        setEditandoValoresPropostas(editPropostas);
      }
      
      if (isAdmin && valoresPropostasParceiroData) {
        setValoresPropostasParceiro(valoresPropostasParceiroData);
        const editPropostasParceiro: EditValoresPropostasParceiro = {};
        valoresPropostasParceiroData.forEach((v: ValoresPropostaParceiro) => {
          if (!editPropostasParceiro[v.parceiro_id]) {
            editPropostasParceiro[v.parceiro_id] = {};
          }
          // Inicializar com valores existentes (agora todos os parceiros têm valores globais inicializados)
          editPropostasParceiro[v.parceiro_id][v.modelo_carregador] = {
            equipamento: formatBrazilianNumber(v.preco_equipamento, 2),
            instalacao: formatBrazilianNumber(v.preco_instalacao, 2),
            materiais: formatBrazilianNumber(v.preco_materiais, 2),
          };
        });
        setEditandoValoresPropostasParceiro(editPropostasParceiro);
      }
      if (isAdmin && comissaoGlobaisData) {
        setComissaoGlobais(comissaoGlobaisData);
        const editGlobais: Record<string, { parceiro: string; parceiroPai: string }> = {};
        comissaoGlobaisData.forEach((g: ComissaoGlobal) => {
          editGlobais[g.modelo_carregador] = {
            parceiro: formatBrazilianNumber(g.valor_comissao_parceiro, 2),
            parceiroPai: formatBrazilianNumber(g.valor_comissao_parceiro_pai, 2),
          };
        });
        setEditandoComissaoGlobais(editGlobais);
      }
      if (comissoesParceiroPaiData) {
        // Admin: array de parceiros pai; Parceiro pai: objeto único
        if (isAdmin && Array.isArray(comissoesParceiroPaiData)) {
          setComissoesParceiroPai(comissoesParceiroPaiData);
          const editPai: EditComissoesParceiroPai = {};
          comissoesParceiroPaiData.forEach((pai: ParceiroPaiComissao) => {
            editPai[pai.parceiro_pai_id] = {};
            pai.filhos.forEach((f: ParceiroFilhoComissaoPai) => {
              editPai[pai.parceiro_pai_id][f.parceiro_filho_id] = {
                "7": formatBrazilianNumber(f.valor_7kw, 2),
                "11": formatBrazilianNumber(f.valor_11kw, 2),
                "22": formatBrazilianNumber(f.valor_22kw, 2),
              };
            });
          });
          setEditandoComissoesParceiroPai(editPai);
        } else if (!isAdmin && comissoesParceiroPaiData && !Array.isArray(comissoesParceiroPaiData)) {
          // Parceiro pai: converter objeto único para array
          setComissoesParceiroPai([comissoesParceiroPaiData]);
          const editPai: EditComissoesParceiroPai = {};
          editPai[comissoesParceiroPaiData.parceiro_pai_id] = {};
          comissoesParceiroPaiData.filhos.forEach((f: ParceiroFilhoComissaoPai) => {
            editPai[comissoesParceiroPaiData.parceiro_pai_id][f.parceiro_filho_id] = {
              "7": formatBrazilianNumber(f.valor_7kw, 2),
              "11": formatBrazilianNumber(f.valor_11kw, 2),
              "22": formatBrazilianNumber(f.valor_22kw, 2),
            };
          });
          setEditandoComissoesParceiroPai(editPai);
        }
      }
      setDirtyPropostasParceiro(false);
      setDirtyPropostasGlobais(false);
      setDirtyComissoes(false);
      setDirtyComissaoPai(false);
      setDirtyComissaoGlobais(false);
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao carregar configurações", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, parceiroId, podeCriarRedeParceiros, toast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const hasAnyUnsaved = dirtyPropostasParceiro || dirtyPropostasGlobais || dirtyComissaoGlobais || dirtyComissoes || dirtyComissaoPai;
  useEffect(() => {
    onHasUnsavedChanges?.(hasAnyUnsaved);
    return () => onHasUnsavedChanges?.(false);
  }, [hasAnyUnsaved, onHasUnsavedChanges]);

  const handleValorChange = (parceiroId: string, modelo: "7" | "11" | "22", valor: string) => {
    setDirtyComissoes(true);
    setEditandoValores((prev) => ({
      ...prev,
      [parceiroId]: { ...(prev[parceiroId] ?? { "7": "0,00", "11": "0,00", "22": "0,00" }), [modelo]: valor },
    }));
  };

  const handleValorBlur = (parceiroId: string, modelo: "7" | "11" | "22") => {
    const raw = editandoValores[parceiroId]?.[modelo] ?? "0";
    const num = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    const valor = isNaN(num) ? 0 : Math.max(0, num);
    setEditandoValores((prev) => ({
      ...prev,
      [parceiroId]: { ...(prev[parceiroId] ?? { "7": "0,00", "11": "0,00", "22": "0,00" }), [modelo]: formatBrazilianNumber(valor, 2) },
    }));
  };

  const handleSalvarSecaoComissoes = async () => {
    if (!dirtyComissoes) return;
    setSavingSection("comissoes");
    try {
      const modelos: { k: "7" | "11" | "22"; m: ModeloCarregador }[] = [
        { k: "7", m: "Wallbox 7kW" },
        { k: "11", m: "Wallbox 11kW" },
        { k: "22", m: "Wallbox 22kW" },
      ];
      for (const parceiroId of Object.keys(editandoValores)) {
        const ev = editandoValores[parceiroId];
        if (!ev) continue;
        for (const { k, m } of modelos) {
          const valor = parseValor(ev[k] ?? "0");
          await salvarComissaoParceiroModelo(parceiroId, m, valor);
        }
      }
      toast({ title: "Comissões atualizadas com sucesso" });
      carregar();
    } catch (e) {
      toast({ title: "Erro ao salvar comissões", variant: "destructive" });
    } finally {
      setSavingSection(null);
    }
  };

  const getConfigByParceiro = (parceiroId: string) =>
    configPorModelo.find((c) => c.id === parceiroId);

  const handleValorPropostaChange = (modelo: string, campo: "equipamento" | "instalacao" | "materiais", valor: string) => {
    setDirtyPropostasGlobais(true);
    setEditandoValoresPropostas((prev) => ({
      ...prev,
      [modelo]: { ...(prev[modelo] ?? { equipamento: "0,00", instalacao: "0,00", materiais: "0,00" }), [campo]: valor },
    }));
  };

  const handleValorPropostaBlur = (modelo: string, campo: "equipamento" | "instalacao" | "materiais") => {
    const raw = editandoValoresPropostas[modelo]?.[campo] ?? "0";
    const num = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    const valor = isNaN(num) ? 0 : Math.max(0, num);
    setEditandoValoresPropostas((prev) => ({
      ...prev,
      [modelo]: { ...(prev[modelo] ?? { equipamento: "0,00", instalacao: "0,00", materiais: "0,00" }), [campo]: formatBrazilianNumber(valor, 2) },
    }));
  };

  const handleSalvarSecaoPropostasGlobais = async () => {
    if (!dirtyPropostasGlobais) return;
    setSavingSection("propostasGlobais");
    try {
      for (const modelo of Object.keys(editandoValoresPropostas)) {
        const ev = editandoValoresPropostas[modelo];
        if (!ev) continue;
        const equipamento = parseValor(ev.equipamento ?? "0");
        const instalacao = parseValor(ev.instalacao ?? "0");
        const materiais = parseValor(ev.materiais ?? "0");
        await atualizarValoresPropostasGlobais(modelo as ModeloCarregador, equipamento, instalacao, materiais);
      }
      toast({ title: "Valores de propostas atualizados com sucesso" });
      carregar();
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao salvar valores de propostas", variant: "destructive" });
    } finally {
      setSavingSection(null);
    }
  };

  const handleCriarNovoModelo = async () => {
    if (!novoModelo.nome.trim()) {
      toast({ title: "Nome do modelo é obrigatório", variant: "destructive" });
      return;
    }
    
    setCriandoModelo(true);
    try {
      const equipamento = parseValor(novoModelo.equipamento || "0");
      const instalacao = parseValor(novoModelo.instalacao || "0");
      const materiais = parseValor(novoModelo.materiais || "0");
      
      await criarNovoModeloValoresPropostasGlobais(
        novoModelo.nome.trim(),
        equipamento,
        instalacao,
        materiais
      );
      
      toast({ title: "Novo modelo criado com sucesso! Os valores serão propagados para todos os parceiros." });
      setIsNovoModeloOpen(false);
      setNovoModelo({ nome: "", equipamento: "", instalacao: "", materiais: "" });
      carregar();
    } catch (e: any) {
      console.error(e);
      toast({ 
        title: "Erro ao criar modelo", 
        description: e.message || "Verifique se o modelo já existe",
        variant: "destructive" 
      });
    } finally {
      setCriandoModelo(false);
    }
  };

  const handleRemoverModelo = async (modelo: string) => {
    if (!confirm(`Tem certeza que deseja remover o modelo "${modelo}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    
    setRemovendoModelo(modelo);
    try {
      await removerModeloValoresPropostasGlobais(modelo);
      toast({ title: "Modelo removido com sucesso" });
      carregar();
    } catch (e: any) {
      console.error(e);
      toast({ 
        title: "Erro ao remover modelo", 
        description: e.message || "Não foi possível remover o modelo",
        variant: "destructive" 
      });
    } finally {
      setRemovendoModelo(null);
    }
  };

  const handleComissaoGlobalChange = (modelo: string, tipo: "parceiro" | "parceiroPai", valor: string) => {
    setDirtyComissaoGlobais(true);
    setEditandoComissaoGlobais((prev) => ({
      ...prev,
      [modelo]: {
        ...(prev[modelo] ?? { parceiro: "0,00", parceiroPai: "0,00" }),
        [tipo]: valor,
      },
    }));
  };

  const handleComissaoGlobalBlur = (modelo: string, tipo: "parceiro" | "parceiroPai") => {
    const raw = editandoComissaoGlobais[modelo]?.[tipo] ?? "0";
    const num = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    const valor = isNaN(num) ? 0 : Math.max(0, num);
    setEditandoComissaoGlobais((prev) => ({
      ...prev,
      [modelo]: {
        ...(prev[modelo] ?? { parceiro: "0,00", parceiroPai: "0,00" }),
        [tipo]: formatBrazilianNumber(valor, 2),
      },
    }));
  };

  const handleSalvarSecaoComissaoGlobais = async () => {
    if (!dirtyComissaoGlobais) return;
    setSavingSection("comissaoGlobais");
    try {
      for (const modelo of Object.keys(editandoComissaoGlobais)) {
        const valores = editandoComissaoGlobais[modelo];
        if (!valores) continue;
        const valorParceiro = parseValor(valores.parceiro ?? "0");
        const valorPai = parseValor(valores.parceiroPai ?? "0");
        await atualizarComissaoGlobal(modelo as ModeloCarregador, valorParceiro, valorPai);
      }
      toast({ title: "Valores padrão globais de comissões atualizados com sucesso" });
      carregar();
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao salvar valores padrão de comissões", variant: "destructive" });
    } finally {
      setSavingSection(null);
    }
  };

  const handleValorPropostaParceiroChange = (parceiroId: string, modelo: string, campo: "equipamento" | "instalacao" | "materiais", valor: string) => {
    setDirtyPropostasParceiro(true);
    setEditandoValoresPropostasParceiro((prev) => ({
      ...prev,
      [parceiroId]: {
        ...(prev[parceiroId] ?? {}),
        [modelo]: {
          ...(prev[parceiroId]?.[modelo] ?? { equipamento: "0,00", instalacao: "0,00", materiais: "0,00" }),
          [campo]: valor,
        },
      },
    }));
  };

  const handleValorPropostaParceiroBlur = (parceiroId: string, modelo: string, campo: "equipamento" | "instalacao" | "materiais") => {
    const raw = editandoValoresPropostasParceiro[parceiroId]?.[modelo]?.[campo] ?? "0";
    const num = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    let valor = isNaN(num) ? 0 : Math.max(0, num);
    const { equipamentoMin, instalacaoMin, materiaisMin } = getMinimosPropostaModelo(modelo);
    const minimoCampo =
      campo === "equipamento"
        ? equipamentoMin
        : campo === "instalacao"
        ? instalacaoMin
        : materiaisMin;
    if (minimoCampo > 0) {
      valor = Math.max(valor, minimoCampo);
    }
    setEditandoValoresPropostasParceiro((prev) => ({
      ...prev,
      [parceiroId]: {
        ...(prev[parceiroId] ?? {}),
        [modelo]: {
          ...(prev[parceiroId]?.[modelo] ?? { equipamento: "0,00", instalacao: "0,00", materiais: "0,00" }),
          [campo]: formatBrazilianNumber(valor, 2),
        },
      },
    }));
  };

  const handleSalvarSecaoPropostasParceiro = async () => {
    if (!dirtyPropostasParceiro) return;
    setSavingSection("propostasParceiro");
    try {
      for (const parceiroId of Object.keys(editandoValoresPropostasParceiro)) {
        for (const modelo of Object.keys(editandoValoresPropostasParceiro[parceiroId] ?? {})) {
          const ev = editandoValoresPropostasParceiro[parceiroId][modelo];
          if (!ev) continue;
          const equipamento = parseValor(ev.equipamento ?? "0");
          const instalacao = parseValor(ev.instalacao ?? "0");
          const materiais = parseValor(ev.materiais ?? "0");
          await atualizarValoresPropostasParceiro(parceiroId, modelo as ModeloCarregador, equipamento, instalacao, materiais);
        }
      }
      toast({ title: "Valores de propostas do parceiro atualizados com sucesso" });
      carregar();
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao salvar valores de propostas do parceiro", variant: "destructive" });
    } finally {
      setSavingSection(null);
    }
  };

  const handleComissaoPaiChange = (parceiroPaiId: string, parceiroFilhoId: string, modelo: "7" | "11" | "22", valor: string) => {
    setDirtyComissaoPai(true);
    setEditandoComissoesParceiroPai((prev) => ({
      ...prev,
      [parceiroPaiId]: {
        ...(prev[parceiroPaiId] ?? {}),
        [parceiroFilhoId]: {
          ...(prev[parceiroPaiId]?.[parceiroFilhoId] ?? { "7": "0,00", "11": "0,00", "22": "0,00" }),
          [modelo]: valor,
        },
      },
    }));
  };

  const handleComissaoPaiBlur = (parceiroPaiId: string, parceiroFilhoId: string, modelo: "7" | "11" | "22") => {
    const raw = editandoComissoesParceiroPai[parceiroPaiId]?.[parceiroFilhoId]?.[modelo] ?? "0";
    const num = parseFloat(raw.replace(/\./g, "").replace(",", "."));
    const valor = isNaN(num) ? 0 : Math.max(0, num);
    setEditandoComissoesParceiroPai((prev) => ({
      ...prev,
      [parceiroPaiId]: {
        ...(prev[parceiroPaiId] ?? {}),
        [parceiroFilhoId]: {
          ...(prev[parceiroPaiId]?.[parceiroFilhoId] ?? { "7": "0,00", "11": "0,00", "22": "0,00" }),
          [modelo]: formatBrazilianNumber(valor, 2),
        },
      },
    }));
  };

  const handleSalvarSecaoComissaoPai = async () => {
    if (!dirtyComissaoPai) return;
    setSavingSection("comissaoPai");
    try {
      const modelos: { k: "7" | "11" | "22"; m: ModeloCarregador }[] = [
        { k: "7", m: "Wallbox 7kW" },
        { k: "11", m: "Wallbox 11kW" },
        { k: "22", m: "Wallbox 22kW" },
      ];
      for (const parceiroPaiId of Object.keys(editandoComissoesParceiroPai)) {
        const filhos = editandoComissoesParceiroPai[parceiroPaiId];
        if (!filhos) continue;
        for (const parceiroFilhoId of Object.keys(filhos)) {
          const ev = filhos[parceiroFilhoId];
          if (!ev) continue;
          for (const { k, m } of modelos) {
            const valor = parseValor(ev[k] ?? "0");
            await salvarComissaoParceiroPai(parceiroPaiId, parceiroFilhoId, m, valor);
          }
        }
      }
      toast({ title: "Comissões por parceiro pai atualizadas com sucesso" });
      carregar();
    } catch (e) {
      console.error(e);
      toast({ title: "Erro ao salvar comissões por parceiro pai", variant: "destructive" });
    } finally {
      setSavingSection(null);
    }
  };

  if (loading && !resumos.length) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-full overflow-x-hidden space-y-6">
      {/* Seção de Valores de Propostas por Parceiro (apenas admin) */}
      {isAdmin && canManage && (
        <Card className="overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Settings className="h-5 w-5 shrink-0 text-primary sm:h-6 sm:w-6" />
                  Valores de Propostas por Parceiro
                </CardTitle>
                <CardDescription className="mt-1.5 text-xs sm:text-sm">
                  Configure os valores de propostas (equipamento, instalação e materiais) para cada parceiro e tipo de wallbox.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSalvarSecaoPropostasParceiro}
                  disabled={!dirtyPropostasParceiro || savingSection === "propostasParceiro"}
                >
                  {savingSection === "propostasParceiro" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar seção
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            {valoresPropostasParceiro.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Carregando parceiros...</p>
            ) : isMobile ? (
              (() => {
                const agrupadoPorParceiro = valoresPropostasParceiro.reduce((acc, v) => {
                  if (!acc[v.parceiro_id]) {
                    acc[v.parceiro_id] = {
                      parceiro_nome: v.parceiro_nome ?? "Sem nome",
                      parceiro_pai_nome: v.parceiro_pai_nome ?? null,
                      modelos: [],
                    };
                  }
                  acc[v.parceiro_id].modelos.push(v);
                  return acc;
                }, {} as Record<string, { parceiro_nome: string; parceiro_pai_nome: string | null; modelos: ValoresPropostaParceiro[] }>);

                return (
                  <div className="space-y-4">
                    {Object.entries(agrupadoPorParceiro).map(([parceiroId, grupo]) => {
                      const isExpanded = !!expandedParceirosPropostas[parceiroId];
                      const toggleExpand = () => {
                        setExpandedParceirosPropostas((prev) => ({ ...prev, [parceiroId]: !prev[parceiroId] }));
                      };
                      return (
                        <Card key={parceiroId} className="overflow-hidden">
                          <button
                            type="button"
                            onClick={toggleExpand}
                            className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                          >
                            <div>
                              <p className="font-medium">{grupo.parceiro_nome}</p>
                              {grupo.parceiro_pai_nome && (
                                <p className="text-xs text-muted-foreground">Pai: {grupo.parceiro_pai_nome}</p>
                              )}
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                            )}
                          </button>
                          {isExpanded && (
                            <CardContent className="pt-0 space-y-4 border-t">
                              {grupo.modelos.map((v) => {
                                const ev = editandoValoresPropostasParceiro[v.parceiro_id]?.[v.modelo_carregador];
                                const valorEquipamento = ev?.equipamento ?? formatBrazilianNumber(v.preco_equipamento, 2);
                                const valorInstalacao = ev?.instalacao ?? formatBrazilianNumber(v.preco_instalacao, 2);
                                const valorMateriais = ev?.materiais ?? formatBrazilianNumber(v.preco_materiais, 2);
                                return (
                                  <div key={`${v.parceiro_id}-${v.modelo_carregador}`} className="rounded-lg border bg-muted/20 p-3 space-y-3">
                                    <p className="font-medium text-sm">{v.modelo_carregador}</p>
                                    <div className="space-y-2">
                                      <div className="flex justify-between items-center gap-2">
                                        <Label className="text-xs text-muted-foreground shrink-0">Equipamento (R$)</Label>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder="0,00"
                                          className="text-right h-9 max-w-[140px]"
                                          value={valorEquipamento}
                                          onChange={(e) => handleValorPropostaParceiroChange(v.parceiro_id, v.modelo_carregador, "equipamento", e.target.value)}
                                          onBlur={() => handleValorPropostaParceiroBlur(v.parceiro_id, v.modelo_carregador, "equipamento")}
                                        />
                                      </div>
                                      <div className="flex justify-between items-center gap-2">
                                        <Label className="text-xs text-muted-foreground shrink-0">Instalação (R$)</Label>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder="0,00"
                                          className="text-right h-9 max-w-[140px]"
                                          value={valorInstalacao}
                                          onChange={(e) => handleValorPropostaParceiroChange(v.parceiro_id, v.modelo_carregador, "instalacao", e.target.value)}
                                          onBlur={() => handleValorPropostaParceiroBlur(v.parceiro_id, v.modelo_carregador, "instalacao")}
                                        />
                                      </div>
                                      <div className="flex justify-between items-center gap-2">
                                        <Label className="text-xs text-muted-foreground shrink-0">Materiais (R$)</Label>
                                        <Input
                                          type="text"
                                          inputMode="decimal"
                                          placeholder="0,00"
                                          className="text-right h-9 max-w-[140px]"
                                          value={valorMateriais}
                                          onChange={(e) => handleValorPropostaParceiroChange(v.parceiro_id, v.modelo_carregador, "materiais", e.target.value)}
                                          onBlur={() => handleValorPropostaParceiroBlur(v.parceiro_id, v.modelo_carregador, "materiais")}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[640px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Parceiro</TableHead>
                      <TableHead className="text-right">Modelo</TableHead>
                      <TableHead className="text-right">Equipamento (R$)</TableHead>
                      <TableHead className="text-right">Instalação (R$)</TableHead>
                      <TableHead className="text-right">Materiais (R$)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const agrupadoPorParceiro = valoresPropostasParceiro.reduce((acc, v) => {
                        if (!acc[v.parceiro_id]) {
                          acc[v.parceiro_id] = {
                            parceiro_nome: v.parceiro_nome ?? "Sem nome",
                            parceiro_pai_nome: v.parceiro_pai_nome ?? null,
                            modelos: [],
                          };
                        }
                        acc[v.parceiro_id].modelos.push(v);
                        return acc;
                      }, {} as Record<string, { parceiro_nome: string; parceiro_pai_nome: string | null; modelos: ValoresPropostaParceiro[] }>);

                      return Object.entries(agrupadoPorParceiro).flatMap(([parceiroId, grupo]) => {
                        const isExpanded = !!expandedParceirosPropostas[parceiroId];
                        const toggleExpand = () => {
                          setExpandedParceirosPropostas((prev) => ({ ...prev, [parceiroId]: !prev[parceiroId] }));
                        };
                        return (
                          <Fragment key={parceiroId}>
                            <TableRow>
                              <TableCell className="w-10 p-1">
                                <Button variant="ghost" size="sm" onClick={toggleExpand} className="h-8 w-8 p-0" aria-label={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}>
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              </TableCell>
                              <TableCell className="font-medium">
                                <span>{grupo.parceiro_nome}</span>
                                {grupo.parceiro_pai_nome && <span className="text-xs text-muted-foreground ml-1">Pai: {grupo.parceiro_pai_nome}</span>}
                              </TableCell>
                              {!isExpanded ? (
                                <TableCell colSpan={4} className="text-muted-foreground text-sm">Clique na seta para ver os valores por tipo de carregador</TableCell>
                              ) : (
                                <><TableCell /><TableCell /><TableCell /><TableCell /></>
                              )}
                            </TableRow>
                            {isExpanded && grupo.modelos.map((v) => {
                              const ev = editandoValoresPropostasParceiro[v.parceiro_id]?.[v.modelo_carregador];
                              const valorEquipamento = ev?.equipamento ?? formatBrazilianNumber(v.preco_equipamento, 2);
                              const valorInstalacao = ev?.instalacao ?? formatBrazilianNumber(v.preco_instalacao, 2);
                              const valorMateriais = ev?.materiais ?? formatBrazilianNumber(v.preco_materiais, 2);
                              return (
                                <TableRow key={`${v.parceiro_id}-${v.modelo_carregador}`} className="bg-muted/30">
                                  <TableCell className="w-10" /><TableCell className="pl-8 text-muted-foreground" />
                                  <TableCell className="font-medium">{v.modelo_carregador}</TableCell>
                                  <TableCell className="text-right">
                                    <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8" value={valorEquipamento}
                                      onChange={(e) => handleValorPropostaParceiroChange(v.parceiro_id, v.modelo_carregador, "equipamento", e.target.value)}
                                      onBlur={() => handleValorPropostaParceiroBlur(v.parceiro_id, v.modelo_carregador, "equipamento")} />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8" value={valorInstalacao}
                                      onChange={(e) => handleValorPropostaParceiroChange(v.parceiro_id, v.modelo_carregador, "instalacao", e.target.value)}
                                      onBlur={() => handleValorPropostaParceiroBlur(v.parceiro_id, v.modelo_carregador, "instalacao")} />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8" value={valorMateriais}
                                      onChange={(e) => handleValorPropostaParceiroChange(v.parceiro_id, v.modelo_carregador, "materiais", e.target.value)}
                                      onBlur={() => handleValorPropostaParceiroBlur(v.parceiro_id, v.modelo_carregador, "materiais")} />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </Fragment>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção de Valores de Propostas Globais (apenas admin) */}
      {isAdmin && canManage && (
        <Card className="overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Settings className="h-5 w-5 shrink-0 text-primary sm:h-6 sm:w-6" />
                  Valores Padrão Globais de Propostas
                </CardTitle>
                <CardDescription className="mt-1.5 text-xs sm:text-sm">
                  Configure os valores padrão globais de propostas (equipamento, instalação e materiais) para cada tipo de wallbox. Estes valores serão usados quando não houver configuração específica para o parceiro.
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
                <Dialog open={isNovoModeloOpen} onOpenChange={setIsNovoModeloOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Plus className="h-4 w-4 mr-2" />
                      Adicionar Modelo
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Adicionar Novo Modelo de Wallbox</DialogTitle>
                      <DialogDescription>
                        Adicione um novo modelo de wallbox com seus valores padrão. O modelo será automaticamente propagado para todos os parceiros.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="nome-modelo">Nome do Modelo *</Label>
                        <Input
                          id="nome-modelo"
                          placeholder="Ex: Wallbox 15kW"
                          value={novoModelo.nome}
                          onChange={(e) => setNovoModelo({ ...novoModelo, nome: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="equipamento">Preço do Equipamento (R$)</Label>
                        <Input
                          id="equipamento"
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={novoModelo.equipamento}
                          onChange={(e) => setNovoModelo({ ...novoModelo, equipamento: e.target.value })}
                          onBlur={(e) => {
                            const num = parseValor(e.target.value);
                            setNovoModelo({ ...novoModelo, equipamento: formatBrazilianNumber(num, 2) });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="instalacao">Preço de Instalação (R$)</Label>
                        <Input
                          id="instalacao"
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={novoModelo.instalacao}
                          onChange={(e) => setNovoModelo({ ...novoModelo, instalacao: e.target.value })}
                          onBlur={(e) => {
                            const num = parseValor(e.target.value);
                            setNovoModelo({ ...novoModelo, instalacao: formatBrazilianNumber(num, 2) });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="materiais">Preço de Materiais (R$)</Label>
                        <Input
                          id="materiais"
                          type="text"
                          inputMode="decimal"
                          placeholder="0,00"
                          value={novoModelo.materiais}
                          onChange={(e) => setNovoModelo({ ...novoModelo, materiais: e.target.value })}
                          onBlur={(e) => {
                            const num = parseValor(e.target.value);
                            setNovoModelo({ ...novoModelo, materiais: formatBrazilianNumber(num, 2) });
                          }}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsNovoModeloOpen(false)}>
                        Cancelar
                      </Button>
                      <Button onClick={handleCriarNovoModelo} disabled={criandoModelo || !novoModelo.nome.trim()}>
                        {criandoModelo ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Criando...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Criar Modelo
                          </>
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button
                  size="sm"
                  onClick={handleSalvarSecaoPropostasGlobais}
                  disabled={!dirtyPropostasGlobais || savingSection === "propostasGlobais"}
                >
                  {savingSection === "propostasGlobais" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar seção
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            {valoresPropostas.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Nenhum valor configurado. Clique em &quot;Adicionar Modelo&quot; para criar o primeiro.</p>
            ) : isMobile ? (
              <div className="space-y-4">
                {valoresPropostas.map((v) => {
                  const ev = editandoValoresPropostas[v.modelo_carregador];
                  return (
                    <Card key={v.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{v.modelo_carregador}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between items-center gap-2">
                          <Label className="text-sm text-muted-foreground shrink-0">Equipamento (R$)</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0,00"
                            className="text-right h-9 max-w-[140px]"
                            value={ev?.equipamento ?? formatBrazilianNumber(v.preco_equipamento, 2)}
                            onChange={(e) => handleValorPropostaChange(v.modelo_carregador, "equipamento", e.target.value)}
                            onBlur={() => handleValorPropostaBlur(v.modelo_carregador, "equipamento")}
                          />
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <Label className="text-sm text-muted-foreground shrink-0">Instalação (R$)</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0,00"
                            className="text-right h-9 max-w-[140px]"
                            value={ev?.instalacao ?? formatBrazilianNumber(v.preco_instalacao, 2)}
                            onChange={(e) => handleValorPropostaChange(v.modelo_carregador, "instalacao", e.target.value)}
                            onBlur={() => handleValorPropostaBlur(v.modelo_carregador, "instalacao")}
                          />
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <Label className="text-sm text-muted-foreground shrink-0">Materiais (R$)</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0,00"
                            className="text-right h-9 max-w-[140px]"
                            value={ev?.materiais ?? formatBrazilianNumber(v.preco_materiais, 2)}
                            onChange={(e) => handleValorPropostaChange(v.modelo_carregador, "materiais", e.target.value)}
                            onBlur={() => handleValorPropostaBlur(v.modelo_carregador, "materiais")}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[480px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Modelo</TableHead>
                      <TableHead className="text-right">Equipamento (R$)</TableHead>
                      <TableHead className="text-right">Instalação (R$)</TableHead>
                      <TableHead className="text-right">Materiais (R$)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {valoresPropostas.map((v) => {
                      const ev = editandoValoresPropostas[v.modelo_carregador];
                      return (
                        <TableRow key={v.id}>
                          <TableCell className="font-medium">{v.modelo_carregador}</TableCell>
                          <TableCell className="text-right">
                            <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8"
                              value={ev?.equipamento ?? formatBrazilianNumber(v.preco_equipamento, 2)}
                              onChange={(e) => handleValorPropostaChange(v.modelo_carregador, "equipamento", e.target.value)}
                              onBlur={() => handleValorPropostaBlur(v.modelo_carregador, "equipamento")} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8"
                              value={ev?.instalacao ?? formatBrazilianNumber(v.preco_instalacao, 2)}
                              onChange={(e) => handleValorPropostaChange(v.modelo_carregador, "instalacao", e.target.value)}
                              onBlur={() => handleValorPropostaBlur(v.modelo_carregador, "instalacao")} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8"
                              value={ev?.materiais ?? formatBrazilianNumber(v.preco_materiais, 2)}
                              onChange={(e) => handleValorPropostaChange(v.modelo_carregador, "materiais", e.target.value)}
                              onBlur={() => handleValorPropostaBlur(v.modelo_carregador, "materiais")} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção de Valores Padrão Globais de Comissões (parceiro pai) - apenas admin */}
      {isAdmin && canManage && (
        <Card className="overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Settings className="h-5 w-5 shrink-0 text-primary sm:h-6 sm:w-6" />
                  Valores Padrão Globais de Comissões
                </CardTitle>
                <CardDescription className="mt-1.5 text-xs sm:text-sm">
                  Configure os valores padrão em R$ de comissão por modelo de wallbox. Do lado esquerdo, valores para parceiros (usados quando um novo parceiro é criado). Do lado direito, valores para parceiros pai (usados como base na seção &quot;Comissões por Parceiro Pai&quot;).
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
                <Button
                  size="sm"
                  onClick={handleSalvarSecaoComissaoGlobais}
                  disabled={!dirtyComissaoGlobais || savingSection === "comissaoGlobais"}
                >
                  {savingSection === "comissaoGlobais" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar seção
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            {comissaoGlobais.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Nenhum valor configurado.</p>
            ) : isMobile ? (
              <div className="space-y-4">
                {comissaoGlobais.map((g) => {
                  const ev = editandoComissaoGlobais[g.modelo_carregador];
                  return (
                    <Card key={g.id}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{g.modelo_carregador}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex justify-between items-center gap-2">
                          <Label className="text-sm text-muted-foreground shrink-0">Comissão Parceiros (R$)</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0,00"
                            className="text-right h-9 max-w-[140px]"
                            value={ev?.parceiro ?? formatBrazilianNumber(g.valor_comissao_parceiro, 2)}
                            onChange={(e) => handleComissaoGlobalChange(g.modelo_carregador, "parceiro", e.target.value)}
                            onBlur={() => handleComissaoGlobalBlur(g.modelo_carregador, "parceiro")}
                          />
                        </div>
                        <div className="flex justify-between items-center gap-2">
                          <Label className="text-sm text-muted-foreground shrink-0">Comissão Parceiros Pai (R$)</Label>
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="0,00"
                            className="text-right h-9 max-w-[140px]"
                            value={ev?.parceiroPai ?? formatBrazilianNumber(g.valor_comissao_parceiro_pai, 2)}
                            onChange={(e) => handleComissaoGlobalChange(g.modelo_carregador, "parceiroPai", e.target.value)}
                            onBlur={() => handleComissaoGlobalBlur(g.modelo_carregador, "parceiroPai")}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[320px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Modelo</TableHead>
                      <TableHead className="text-right">Comissão Parceiros (R$)</TableHead>
                      <TableHead className="text-right">Comissão Parceiros Pai (R$)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comissaoGlobais.map((g) => {
                      const ev = editandoComissaoGlobais[g.modelo_carregador];
                      return (
                        <TableRow key={g.id}>
                          <TableCell className="font-medium">{g.modelo_carregador}</TableCell>
                          <TableCell className="text-right">
                            <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8"
                              value={ev?.parceiro ?? formatBrazilianNumber(g.valor_comissao_parceiro, 2)}
                              onChange={(e) => handleComissaoGlobalChange(g.modelo_carregador, "parceiro", e.target.value)}
                              onBlur={() => handleComissaoGlobalBlur(g.modelo_carregador, "parceiro")} />
                          </TableCell>
                          <TableCell className="text-right">
                            <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[120px] ml-auto h-8"
                              value={ev?.parceiroPai ?? formatBrazilianNumber(g.valor_comissao_parceiro_pai, 2)}
                              onChange={(e) => handleComissaoGlobalChange(g.modelo_carregador, "parceiroPai", e.target.value)}
                              onBlur={() => handleComissaoGlobalBlur(g.modelo_carregador, "parceiroPai")} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Seção de Comissões */}
      <Card className="overflow-hidden">
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                {isAdmin ? "Resumo de Comissões por Parceiro" : podeCriarRedeParceiros ? "Resumo de Comissões por Parceiro" : "Meu Resumo de Comissões"}
              </CardTitle>
              <CardDescription className="mt-1.5 text-xs sm:text-sm">
                {isAdmin || podeCriarRedeParceiros
                  ? "Configure o valor em R$ por modelo (7kW, 11kW, 22kW). Total calculado pelos contratos fechados."
                  : "Seus contratos fechados, total de comissões, pagamentos e saldo a receber."}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
              <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Atualizar
              </Button>
              {(isAdmin || podeCriarRedeParceiros) && canManage && (
                <Button
                  size="sm"
                  onClick={handleSalvarSecaoComissoes}
                  disabled={!dirtyComissoes || savingSection === "comissoes"}
                >
                  {savingSection === "comissoes" ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar seção
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          {isAdmin || podeCriarRedeParceiros ? (
            resumos.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">Nenhum parceiro cadastrado.</p>
            ) : isMobile ? (
              (() => {
                const totaisComoPaiByParceiroId = (comissoesParceiroPai ?? []).reduce(
                  (acc, pai) => {
                    const total = pai.filhos.reduce(
                      (s, f) => ({
                        total_comissoes: s.total_comissoes + (f.total_comissoes ?? 0),
                        total_pago: s.total_pago + (f.total_pago ?? 0),
                        saldo: s.saldo + (f.saldo ?? 0),
                        qtd_contratos: s.qtd_contratos + (f.qtd_contratos ?? 0),
                      }),
                      { total_comissoes: 0, total_pago: 0, saldo: 0, qtd_contratos: 0 }
                    );
                    acc[pai.parceiro_pai_id] = total;
                    return acc;
                  },
                  {} as Record<string, { total_comissoes: number; total_pago: number; saldo: number; qtd_contratos: number }>
                );
                const parceirosQueSaoPai = new Set((comissoesParceiroPai ?? []).map((p) => p.parceiro_pai_id));

                return (
                  <div className="space-y-4">
                    {resumos.map((r) => {
                      const cfg = getConfigByParceiro(r.parceiro_id);
                      const ev = editandoValores[r.parceiro_id];
                      const isParceiroPai = parceirosQueSaoPai.has(r.parceiro_id);
                      const totaisPai = totaisComoPaiByParceiroId[r.parceiro_id];
                      const isExpanded = !!expandedResumoComissao[r.parceiro_id];
                      const toggleExpand = () => {
                        setExpandedResumoComissao((prev) => ({ ...prev, [r.parceiro_id]: !prev[r.parceiro_id] }));
                      };
                      return (
                        <Card key={r.parceiro_id}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <CardTitle className="text-base">{r.parceiro_nome}</CardTitle>
                                {r.parceiro_pai_nome && (
                                  <CardDescription className="mt-0.5">Pai: {r.parceiro_pai_nome}</CardDescription>
                                )}
                              </div>
                              {isParceiroPai && (
                                <Button variant="ghost" size="sm" onClick={toggleExpand} className="h-8 w-8 p-0 shrink-0"
                                  aria-label={isExpanded ? "Recolher comissões como parceiro pai" : "Expandir comissões como parceiro pai"}>
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              {(["7", "11", "22"] as const).map((k) => (
                                <div key={k} className="flex justify-between items-center gap-2">
                                  <span className="text-muted-foreground">{k} kW (R$)</span>
                                  {(canManage && (isAdmin || podeCriarRedeParceiros)) ? (
                                    <Input
                                      type="text"
                                      inputMode="decimal"
                                      placeholder="0,00"
                                      className="text-right h-9 max-w-[100px]"
                                      value={ev?.[k] ?? formatBrazilianNumber(k === "7" ? (cfg?.valor_7kw ?? 0) : k === "11" ? (cfg?.valor_11kw ?? 0) : (cfg?.valor_22kw ?? 0), 2)}
                                      onChange={(e) => handleValorChange(r.parceiro_id, k, e.target.value)}
                                      onBlur={() => handleValorBlur(r.parceiro_id, k)}
                                    />
                                  ) : (
                                    <span className="font-medium">{formatBrazilianCurrency(k === "7" ? (cfg?.valor_7kw ?? 0) : k === "11" ? (cfg?.valor_11kw ?? 0) : (cfg?.valor_22kw ?? 0))}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between text-sm pt-2 border-t">
                              <span className="text-muted-foreground">Nº contratos</span>
                              <span className="font-medium">{r.qtd_contratos}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Total comissões</span>
                              <span className="font-medium">{formatBrazilianCurrency(r.total_comissoes)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-muted-foreground">Total pago</span>
                              <span className="font-medium">{formatBrazilianCurrency(r.total_pago)}</span>
                            </div>
                            <div className="flex justify-between text-sm font-medium text-primary">
                              <span>Saldo</span>
                              <span>{formatBrazilianCurrency(r.saldo)}</span>
                            </div>
                            {isParceiroPai && isExpanded && totaisPai && (
                              <>
                                <div className="rounded-lg bg-muted/30 p-3 mt-2 space-y-2">
                                  <p className="text-xs font-medium text-muted-foreground">Comissões como parceiro pai</p>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Nº contratos</span>
                                    <span>{totaisPai.qtd_contratos}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Total comissões</span>
                                    <span>{formatBrazilianCurrency(totaisPai.total_comissoes)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Total pago</span>
                                    <span>{formatBrazilianCurrency(totaisPai.total_pago)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm font-medium text-primary">
                                    <span>Saldo</span>
                                    <span>{formatBrazilianCurrency(totaisPai.saldo)}</span>
                                  </div>
                                </div>
                                <div className="flex justify-between text-sm font-semibold pt-2 border-t bg-muted/20 rounded p-2">
                                  <span>Total geral</span>
                                  <span className="text-primary">{formatBrazilianCurrency(r.total_comissoes + totaisPai.total_comissoes)}</span>
                                </div>
                              </>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                );
              })()
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Parceiro</TableHead>
                      <TableHead className="text-right">7 kW (R$)</TableHead>
                      <TableHead className="text-right">11 kW (R$)</TableHead>
                      <TableHead className="text-right">22 kW (R$)</TableHead>
                      <TableHead className="text-right">Nº contratos</TableHead>
                      <TableHead className="text-right">Total comissões</TableHead>
                      <TableHead className="text-right">Total pago</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const totaisComoPaiByParceiroId = (comissoesParceiroPai ?? []).reduce(
                        (acc, pai) => {
                          const total = pai.filhos.reduce(
                            (s, f) => ({
                              total_comissoes: s.total_comissoes + (f.total_comissoes ?? 0),
                              total_pago: s.total_pago + (f.total_pago ?? 0),
                              saldo: s.saldo + (f.saldo ?? 0),
                              qtd_contratos: s.qtd_contratos + (f.qtd_contratos ?? 0),
                            }),
                            { total_comissoes: 0, total_pago: 0, saldo: 0, qtd_contratos: 0 }
                          );
                          acc[pai.parceiro_pai_id] = total;
                          return acc;
                        },
                        {} as Record<string, { total_comissoes: number; total_pago: number; saldo: number; qtd_contratos: number }>
                      );
                      const parceirosQueSaoPai = new Set((comissoesParceiroPai ?? []).map((p) => p.parceiro_pai_id));
                      return (
                        <>
                          {resumos.map((r) => {
                            const cfg = getConfigByParceiro(r.parceiro_id);
                            const ev = editandoValores[r.parceiro_id];
                            const isParceiroPai = parceirosQueSaoPai.has(r.parceiro_id);
                            const totaisPai = totaisComoPaiByParceiroId[r.parceiro_id];
                            const isExpanded = !!expandedResumoComissao[r.parceiro_id];
                            const toggleExpand = () => {
                              setExpandedResumoComissao((prev) => ({ ...prev, [r.parceiro_id]: !prev[r.parceiro_id] }));
                            };
                            return (
                              <Fragment key={r.parceiro_id}>
                                <TableRow>
                                  <TableCell className="w-10 p-1">
                                    {isParceiroPai ? (
                                      <Button variant="ghost" size="sm" onClick={toggleExpand} className="h-8 w-8 p-0" aria-label={isExpanded ? "Recolher comissões como parceiro pai" : "Expandir comissões como parceiro pai"}>
                                        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </Button>
                                    ) : null}
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <span>{r.parceiro_nome}</span>
                                    {r.parceiro_pai_nome && <span className="text-xs text-muted-foreground ml-1">Pai: {r.parceiro_pai_nome}</span>}
                                  </TableCell>
                                  {(["7", "11", "22"] as const).map((k) => (
                                    <TableCell key={k} className="text-right">
                                      {(canManage && (isAdmin || podeCriarRedeParceiros)) ? (
                                        <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[100px] ml-auto h-8"
                                          value={ev?.[k] ?? formatBrazilianNumber(k === "7" ? (cfg?.valor_7kw ?? 0) : k === "11" ? (cfg?.valor_11kw ?? 0) : (cfg?.valor_22kw ?? 0), 2)}
                                          onChange={(e) => handleValorChange(r.parceiro_id, k, e.target.value)}
                                          onBlur={() => handleValorBlur(r.parceiro_id, k)} />
                                      ) : (
                                        formatBrazilianCurrency(k === "7" ? (cfg?.valor_7kw ?? 0) : k === "11" ? (cfg?.valor_11kw ?? 0) : (cfg?.valor_22kw ?? 0))
                                      )}
                                    </TableCell>
                                  ))}
                                  <TableCell className="text-right">{r.qtd_contratos}</TableCell>
                                  <TableCell className="text-right font-medium">{formatBrazilianCurrency(r.total_comissoes)}</TableCell>
                                  <TableCell className="text-right">{formatBrazilianCurrency(r.total_pago)}</TableCell>
                                  <TableCell className="text-right font-medium text-primary">{formatBrazilianCurrency(r.saldo)}</TableCell>
                                </TableRow>
                                {isParceiroPai && isExpanded && totaisPai && (
                                  <>
                                    <TableRow className="bg-muted/30">
                                      <TableCell className="w-10" />
                                      <TableCell className="pl-8 text-muted-foreground font-medium">Comissões como parceiro pai</TableCell>
                                      <TableCell className="text-right" /><TableCell className="text-right" /><TableCell className="text-right" />
                                      <TableCell className="text-right">{totaisPai.qtd_contratos}</TableCell>
                                      <TableCell className="text-right font-medium">{formatBrazilianCurrency(totaisPai.total_comissoes)}</TableCell>
                                      <TableCell className="text-right">{formatBrazilianCurrency(totaisPai.total_pago)}</TableCell>
                                      <TableCell className="text-right font-medium text-primary">{formatBrazilianCurrency(totaisPai.saldo)}</TableCell>
                                    </TableRow>
                                    <TableRow className="border-t font-semibold bg-muted/20">
                                      <TableCell className="w-10" /><TableCell className="pl-8 font-medium">Total</TableCell>
                                      <TableCell className="text-right" /><TableCell className="text-right" /><TableCell className="text-right" />
                                      <TableCell className="text-right">—</TableCell>
                                      <TableCell className="text-right">{formatBrazilianCurrency(r.total_comissoes + totaisPai.total_comissoes)}</TableCell>
                                      <TableCell className="text-right">{formatBrazilianCurrency(r.total_pago + totaisPai.total_pago)}</TableCell>
                                      <TableCell className="text-right font-medium text-primary">{formatBrazilianCurrency(r.saldo + totaisPai.saldo)}</TableCell>
                                    </TableRow>
                                  </>
                                )}
                              </Fragment>
                            );
                          })}
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="space-y-4">
              {resumos[0] ? (
                <>
                  {minhaConfig && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Valores configurados por modelo</CardTitle>
                        <CardDescription>Comissão em R$ por contrato conforme modelo do Wallbox</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="grid gap-4 sm:grid-cols-3">
                          <div className="flex items-center justify-between rounded-lg border p-4">
                            <span className="font-medium">Wallbox 7 kW</span>
                            <span className="text-lg font-bold">{formatBrazilianCurrency(minhaConfig.valor_7kw)}</span>
                          </div>
                          <div className="flex items-center justify-between rounded-lg border p-4">
                            <span className="font-medium">Wallbox 11 kW</span>
                            <span className="text-lg font-bold">{formatBrazilianCurrency(minhaConfig.valor_11kw)}</span>
                          </div>
                          <div className="flex items-center justify-between rounded-lg border p-4">
                            <span className="font-medium">Wallbox 22 kW</span>
                            <span className="text-lg font-bold">{formatBrazilianCurrency(minhaConfig.valor_22kw)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Contratos fechados
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{resumos[0].qtd_contratos}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Total de comissões
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{formatBrazilianCurrency(resumos[0].total_comissoes)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Total pago
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold">{formatBrazilianCurrency(resumos[0].total_pago)}</p>
                      </CardContent>
                    </Card>
                    <Card className="border-primary bg-primary/5">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">
                          Saldo a receber
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-2xl font-bold text-primary">{formatBrazilianCurrency(resumos[0].saldo)}</p>
                      </CardContent>
                    </Card>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">Nenhum dado de comissão para seu perfil.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Seção de Comissões por Parceiro Pai (admin ou parceiro pai com rede) - visível em edição e visualização */}
      {((isAdmin || podeCriarRedeParceiros) && comissoesParceiroPai.length > 0) && (
        <Card className="overflow-hidden">
          <CardHeader className="p-4 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0 flex-1">
                <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                  <Settings className="h-5 w-5 shrink-0 text-primary sm:h-6 sm:w-6" />
                  Comissões por Parceiro Pai
                </CardTitle>
                <CardDescription className="mt-1.5 text-xs sm:text-sm">
                  {isAdmin 
                    ? "Configure o valor em R$ que cada parceiro pai recebe por contrato fechado por cada parceiro de sua rede (por modelo 7kW, 11kW, 22kW)."
                    : canManage
                    ? "Configure o valor em R$ que você recebe por contrato fechado por cada parceiro de sua rede (por modelo 7kW, 11kW, 22kW)."
                    : "Valor em R$ que você recebe por contrato fechado por cada parceiro de sua rede (por modelo 7kW, 11kW, 22kW)."}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                <Button variant="outline" size="sm" onClick={carregar} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                  Atualizar
                </Button>
                {canManage && (
                  <Button
                    size="sm"
                    onClick={handleSalvarSecaoComissaoPai}
                    disabled={!dirtyComissaoPai || savingSection === "comissaoPai"}
                  >
                    {savingSection === "comissaoPai" ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Salvar seção
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
            {comissoesParceiroPai.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                {isAdmin ? "Nenhum parceiro com rede cadastrado. Parceiros com \"Pode criar rede de parceiros\" ativo aparecem aqui." : "Você ainda não possui parceiros em sua rede."}
              </p>
            ) : isMobile ? (
              <div className="space-y-4">
                {comissoesParceiroPai
                  .filter((pai) => isAdmin || parceiroId === pai.parceiro_pai_id)
                  .map((pai) => {
                  const isParceiroPaiView = !isAdmin && parceiroId === pai.parceiro_pai_id;
                  if (isParceiroPaiView && pai.filhos.length === 0) {
                    return (
                      <Card key={pai.parceiro_pai_id}>
                        <CardContent className="py-6">
                          <p className="text-center text-muted-foreground">Você ainda não possui parceiros em sua rede.</p>
                        </CardContent>
                      </Card>
                    );
                  }
                  const isExpanded = !!expandedParceirosPaiComissao[pai.parceiro_pai_id];
                  const shouldShowExpanded = isParceiroPaiView ? true : isExpanded;
                  const toggleExpand = () => {
                    setExpandedParceirosPaiComissao((prev) => ({ ...prev, [pai.parceiro_pai_id]: !prev[pai.parceiro_pai_id] }));
                  };
                  return (
                    <Card key={pai.parceiro_pai_id}>
                      <button
                        type="button"
                        onClick={() => !isParceiroPaiView && toggleExpand()}
                        className={`w-full text-left p-4 ${!isParceiroPaiView ? "hover:bg-muted/50 transition-colors" : ""}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <CardTitle className="text-base">{pai.parceiro_pai_nome}</CardTitle>
                          {!isParceiroPaiView && (
                            shouldShowExpanded ? (
                              <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
                            )
                          )}
                        </div>
                        {!shouldShowExpanded && !isParceiroPaiView && (
                          <CardDescription className="mt-1">
                            {pai.filhos.length === 0 ? "Nenhum parceiro na rede" : `${pai.filhos.length} parceiro(s) na rede — toque para expandir`}
                          </CardDescription>
                        )}
                      </button>
                      {shouldShowExpanded && (
                        <CardContent className="pt-0 space-y-3 border-t">
                          {pai.filhos.map((filho) => {
                            const ev = editandoComissoesParceiroPai[pai.parceiro_pai_id]?.[filho.parceiro_filho_id];
                            const v7 = ev?.["7"] ?? formatBrazilianNumber(filho.valor_7kw, 2);
                            const v11 = ev?.["11"] ?? formatBrazilianNumber(filho.valor_11kw, 2);
                            const v22 = ev?.["22"] ?? formatBrazilianNumber(filho.valor_22kw, 2);
                            const podeEditar = canManage;
                            return (
                              <div key={`${pai.parceiro_pai_id}-${filho.parceiro_filho_id}`} className="rounded-lg border bg-muted/20 p-3 space-y-3">
                                <p className="font-medium text-sm">{filho.parceiro_filho_nome}</p>
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="flex justify-between items-center gap-2">
                                    <span className="text-muted-foreground">7 kW (R$)</span>
                                    {podeEditar ? (
                                      <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right h-9 max-w-[100px]" value={v7}
                                        onChange={(e) => handleComissaoPaiChange(pai.parceiro_pai_id, filho.parceiro_filho_id, "7", e.target.value)}
                                        onBlur={() => handleComissaoPaiBlur(pai.parceiro_pai_id, filho.parceiro_filho_id, "7")} />
                                    ) : (
                                      <span className="font-medium">{formatBrazilianCurrency(filho.valor_7kw)}</span>
                                    )}
                                  </div>
                                  <div className="flex justify-between items-center gap-2">
                                    <span className="text-muted-foreground">11 kW (R$)</span>
                                    {podeEditar ? (
                                      <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right h-9 max-w-[100px]" value={v11}
                                        onChange={(e) => handleComissaoPaiChange(pai.parceiro_pai_id, filho.parceiro_filho_id, "11", e.target.value)}
                                        onBlur={() => handleComissaoPaiBlur(pai.parceiro_pai_id, filho.parceiro_filho_id, "11")} />
                                    ) : (
                                      <span className="font-medium">{formatBrazilianCurrency(filho.valor_11kw)}</span>
                                    )}
                                  </div>
                                  <div className="flex justify-between items-center gap-2">
                                    <span className="text-muted-foreground">22 kW (R$)</span>
                                    {podeEditar ? (
                                      <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right h-9 max-w-[100px]" value={v22}
                                        onChange={(e) => handleComissaoPaiChange(pai.parceiro_pai_id, filho.parceiro_filho_id, "22", e.target.value)}
                                        onBlur={() => handleComissaoPaiBlur(pai.parceiro_pai_id, filho.parceiro_filho_id, "22")} />
                                    ) : (
                                      <span className="font-medium">{formatBrazilianCurrency(filho.valor_22kw)}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex justify-between text-sm pt-2 border-t">
                                  <span className="text-muted-foreground">Nº contratos</span>
                                  <span>{filho.qtd_contratos ?? 0}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Total comissões</span>
                                  <span>{formatBrazilianCurrency(filho.total_comissoes ?? 0)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">Total pago</span>
                                  <span>{formatBrazilianCurrency(filho.total_pago ?? 0)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-medium text-primary">
                                  <span>Saldo</span>
                                  <span>{formatBrazilianCurrency(filho.saldo ?? 0)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2 sm:mx-0">
                <Table className="min-w-[800px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Parceiro Pai</TableHead>
                      <TableHead className="text-right">Parceiro (rede)</TableHead>
                      <TableHead className="text-right">7 kW (R$)</TableHead>
                      <TableHead className="text-right">11 kW (R$)</TableHead>
                      <TableHead className="text-right">22 kW (R$)</TableHead>
                      <TableHead className="text-right">Nº contratos</TableHead>
                      <TableHead className="text-right">Total comissões</TableHead>
                      <TableHead className="text-right">Total pago</TableHead>
                      <TableHead className="text-right">Saldo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {comissoesParceiroPai.map((pai) => {
                      const isParceiroPaiView = !isAdmin && parceiroId === pai.parceiro_pai_id;
                      if (isParceiroPaiView && pai.filhos.length === 0) {
                        return (
                          <TableRow key={pai.parceiro_pai_id}>
                            <TableCell colSpan={10} className="text-center text-muted-foreground">Você ainda não possui parceiros em sua rede.</TableCell>
                          </TableRow>
                        );
                      }
                      const isExpanded = !!expandedParceirosPaiComissao[pai.parceiro_pai_id];
                      const toggleExpand = () => {
                        setExpandedParceirosPaiComissao((prev) => ({ ...prev, [pai.parceiro_pai_id]: !prev[pai.parceiro_pai_id] }));
                      };
                      const shouldShowExpanded = isParceiroPaiView ? true : isExpanded;
                      return (
                        <Fragment key={pai.parceiro_pai_id}>
                          <TableRow>
                            {!isParceiroPaiView && (
                              <TableCell className="w-10 p-1">
                                <Button variant="ghost" size="sm" onClick={toggleExpand} className="h-8 w-8 p-0" aria-label={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}>
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              </TableCell>
                            )}
                            {isParceiroPaiView && <TableCell className="w-10" />}
                            <TableCell className="font-medium">{pai.parceiro_pai_nome}</TableCell>
                            {!shouldShowExpanded ? (
                              <TableCell colSpan={isParceiroPaiView ? 9 : 8} className="text-muted-foreground text-sm">
                                {pai.filhos.length === 0 ? "Nenhum parceiro na rede" : canManage ? `Clique na seta para configurar comissões dos ${pai.filhos.length} parceiro(s) da rede` : `Comissão por contrato dos ${pai.filhos.length} parceiro(s) da rede (expandido abaixo)`}
                              </TableCell>
                            ) : (
                              <><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /></>
                            )}
                          </TableRow>
                          {shouldShowExpanded && pai.filhos.map((filho) => {
                            const ev = editandoComissoesParceiroPai[pai.parceiro_pai_id]?.[filho.parceiro_filho_id];
                            const v7 = ev?.["7"] ?? formatBrazilianNumber(filho.valor_7kw, 2);
                            const v11 = ev?.["11"] ?? formatBrazilianNumber(filho.valor_11kw, 2);
                            const v22 = ev?.["22"] ?? formatBrazilianNumber(filho.valor_22kw, 2);
                            const podeEditar = canManage;
                            return (
                              <TableRow key={`${pai.parceiro_pai_id}-${filho.parceiro_filho_id}`} className="bg-muted/30">
                                <TableCell className="w-10" /><TableCell className="pl-8 text-muted-foreground" />
                                <TableCell className="font-medium">{filho.parceiro_filho_nome}</TableCell>
                                <TableCell className="text-right">
                                  {podeEditar ? (
                                    <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[100px] ml-auto h-8" value={v7}
                                      onChange={(e) => handleComissaoPaiChange(pai.parceiro_pai_id, filho.parceiro_filho_id, "7", e.target.value)}
                                      onBlur={() => handleComissaoPaiBlur(pai.parceiro_pai_id, filho.parceiro_filho_id, "7")} />
                                  ) : formatBrazilianCurrency(filho.valor_7kw)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {podeEditar ? (
                                    <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[100px] ml-auto h-8" value={v11}
                                      onChange={(e) => handleComissaoPaiChange(pai.parceiro_pai_id, filho.parceiro_filho_id, "11", e.target.value)}
                                      onBlur={() => handleComissaoPaiBlur(pai.parceiro_pai_id, filho.parceiro_filho_id, "11")} />
                                  ) : formatBrazilianCurrency(filho.valor_11kw)}
                                </TableCell>
                                <TableCell className="text-right">
                                  {podeEditar ? (
                                    <Input type="text" inputMode="decimal" placeholder="0,00" className="text-right max-w-[100px] ml-auto h-8" value={v22}
                                      onChange={(e) => handleComissaoPaiChange(pai.parceiro_pai_id, filho.parceiro_filho_id, "22", e.target.value)}
                                      onBlur={() => handleComissaoPaiBlur(pai.parceiro_pai_id, filho.parceiro_filho_id, "22")} />
                                  ) : formatBrazilianCurrency(filho.valor_22kw)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">{filho.qtd_contratos ?? 0}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatBrazilianCurrency(filho.total_comissoes ?? 0)}</TableCell>
                                <TableCell className="text-right tabular-nums">{formatBrazilianCurrency(filho.total_pago ?? 0)}</TableCell>
                                <TableCell className="text-right tabular-nums font-medium">{formatBrazilianCurrency(filho.saldo ?? 0)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
